import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  TbAlertTriangle,
  TbArrowRight,
  TbBrandWhatsapp,
  TbBuilding,
  TbBuildingBank,
  TbChevronDown,
  TbClock,
  TbFileSearch,
  TbLock,
  TbMail,
  TbMapPin,
  TbMessageCircle,
  TbPackage,
  TbPhone,
  TbScale,
  TbShieldCheck,
  TbUsers,
  TbZoomCheck
} from 'react-icons/tb';
import api from '../services/api';
import { log } from '../utils/logger';
import { JsonLd } from '../utils/seo';
import { LAWYERS, OFFICE, getTelLink, getWhatsAppLink } from '../data/escritorio';
import { GUIAS } from '../data/guias';
import { FAQ } from '../data/faq';
import Photo from '../components/Photo';
import LawyerChooser from '../components/LawyerChooser';
import GuideCard from '../components/GuideCard';
import './Home.css';

const AREAS = [
  {
    id: 'criminal',
    icon: TbScale,
    title: 'Direito Criminal',
    summary: 'Investigações, ações penais, habeas corpus e recursos, na Justiça Estadual e Federal.',
    paragraphs: [
      'Atuação estratégica e especializada em todas as áreas do Direito Penal, com experiência em investigações criminais, ações penais, habeas corpus, recursos e acompanhamento completo em procedimentos de alta complexidade.',
      'O escritório possui ampla atuação em processos relacionados à Lei de Drogas, Tribunal do Júri, organização criminosa, crimes patrimoniais, lavagem de capitais, contrabando e demais infrações de competência da Justiça Estadual e Federal.',
      'A Navarro & Vieira Advogados também atua em grandes operações de âmbito nacional, processos com múltiplos investigados e demandas estratégicas perante tribunais estaduais e superiores, sempre com atuação técnica, personalizada e voltada à defesa dos direitos e garantias fundamentais dos clientes.'
    ]
  },
  {
    id: 'juri',
    icon: TbBuildingBank,
    title: 'Tribunal do Júri',
    summary: 'Da fase investigativa ao plenário, em crimes dolosos contra a vida.',
    paragraphs: [
      'Atuação especializada em procedimentos do Tribunal do Júri, desde a fase investigativa até plenário, com preparação estratégica da defesa, análise aprofundada das provas e condução técnica em todas as etapas do processo.',
      'O escritório atua em crimes dolosos contra a vida, oferecendo acompanhamento completo e defesa personalizada em casos de alta complexidade.'
    ]
  },
  {
    id: 'drogas',
    icon: TbZoomCheck,
    title: 'Lei de Drogas e Organização Criminosa',
    summary: 'Investigações complexas, interceptações, medidas cautelares e ações com múltiplos investigados.',
    paragraphs: [
      'Experiência consolidada na atuação em processos relacionados à Lei nº 11.343/2006 e à Lei de Organizações Criminosas, incluindo investigações complexas, interceptações telefônicas, medidas cautelares, operações policiais e ações penais com múltiplos investigados.',
      'Atuação estratégica em processos de grande repercussão e demandas envolvendo Justiça Estadual e Federal.'
    ]
  },
  {
    id: 'federais',
    icon: TbPackage,
    title: 'Contrabando e Crimes Federais',
    summary: 'Contrabando, descaminho e procedimentos administrativos perante a Receita Federal.',
    paragraphs: [
      'Atuação em procedimentos investigatórios e ações penais envolvendo contrabando, descaminho e demais crimes de competência da Justiça Federal.',
      'O escritório também realiza acompanhamento estratégico em procedimentos administrativos perante a Receita Federal do Brasil, com atuação técnica e personalizada conforme a complexidade de cada demanda.'
    ]
  },
  {
    id: 'execucao',
    icon: TbLock,
    title: 'Execução Penal',
    summary: 'Progressão de regime, livramento condicional, remição, detração e demais incidentes da execução.',
    paragraphs: [
      'A Navarro & Vieira Advogados possui ampla atuação na área de Execução Penal, acompanhando o cliente em todas as fases do cumprimento da pena, com atuação estratégica e acompanhamento individualizado perante as Varas de Execução Penal.',
      'O escritório atua em pedidos de progressão de regime, livramento condicional, detração penal, remição de pena, unificação de penas, saídas temporárias, conversão de penas, defesa em procedimentos administrativos disciplinares e demais incidentes relacionados à execução da pena.',
      'Com vasta experiência no acompanhamento de pessoas privadas de liberdade, a atuação é pautada na análise técnica detalhada da execução penal, fiscalização do correto cumprimento da pena pelo Estado e defesa permanente dos direitos e garantias legais do apenado.'
    ]
  },
  {
    id: 'parcerias',
    icon: TbUsers,
    title: 'Cível, Trabalhista e Previdenciário',
    summary: 'Em parceria com profissionais de reconhecida experiência em cada área.',
    paragraphs: [
      'Nas áreas cível, trabalhista e previdenciária, o escritório atua em parceria com profissionais de reconhecida experiência e relevância em suas respectivas áreas de atuação, proporcionando atendimento completo, estratégico e multidisciplinar aos clientes.',
      'A atuação conjunta permite oferecer soluções jurídicas seguras, personalizadas e alinhadas às necessidades específicas de cada caso.'
    ]
  }
];

const HIGHLIGHTS = [
  { value: 'Desde 2020', label: 'Sociedade de advogados fundada em janeiro de 2020' },
  { value: 'Estadual e Federal', label: 'Atuação nas duas Justiças' },
  { value: 'STJ e STF', label: 'Experiência nos tribunais superiores' },
  { value: 'OAB/PR 15.538', label: 'Registro da sociedade' }
];

const PILLARS = [
  { title: 'Técnica', text: 'Análise jurídica aprofundada de cada processo.' },
  { title: 'Estratégia', text: 'Teses defensivas construídas para o caso concreto.' },
  { title: 'Discrição', text: 'Sigilo e respeito em todas as etapas.' },
  { title: 'Compromisso', text: 'Dedicação integral, com ética e transparência.' }
];

const STEPS = [
  {
    icon: TbMessageCircle,
    title: 'Primeiro contato',
    text: 'Pelo WhatsApp, por telefone ou no escritório. Você relata a situação e recebe orientação sobre os primeiros passos.'
  },
  {
    icon: TbFileSearch,
    title: 'Análise do caso',
    text: 'Estudamos os documentos, o inquérito ou o processo para entender a situação com precisão.'
  },
  {
    icon: TbUsers,
    title: 'Reunião e estratégia',
    text: 'Explicamos com clareza os caminhos possíveis, os riscos de cada um e a estratégia de defesa.'
  },
  {
    icon: TbShieldCheck,
    title: 'Acompanhamento',
    text: 'Você recebe retorno sobre audiências, decisões e prazos em cada fase do processo.'
  }
];

const FAQ_JSON_LD = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: FAQ.map((item) => ({
    '@type': 'Question',
    name: item.q,
    acceptedAnswer: { '@type': 'Answer', text: item.a }
  }))
};

const newsExcerpt = (item) => {
  const raw = item.summary || item.excerpt || item.content || '';
  const text = String(raw).replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
  return text.length > 160 ? `${text.slice(0, 157)}…` : text;
};

const AreaCard = ({ area, open, onToggle }) => {
  const Icon = area.icon;
  const panelId = `nvh-area-${area.id}`;
  return (
    <article className={`nvh-area${open ? ' is-open' : ''}`}>
      <Icon className="nvh-area-icon" aria-hidden="true" />
      <h3>{area.title}</h3>
      <p className="nvh-area-summary">{area.summary}</p>
      {open && (
        <div id={panelId} className="nvh-area-full">
          {area.paragraphs.map((p) => (
            <p key={p.slice(0, 40)}>{p}</p>
          ))}
        </div>
      )}
      <button type="button" className="nvh-area-toggle" aria-expanded={open} aria-controls={panelId} onClick={onToggle}>
        {open ? 'Mostrar menos' : 'Saiba mais'}
        <TbChevronDown className="nvh-area-chevron" aria-hidden="true" />
      </button>
    </article>
  );
};

const FaqItem = ({ item, index, open, onToggle }) => {
  const panelId = `nvh-faq-${index}`;
  return (
    <div className={`nvh-faq-item${open ? ' is-open' : ''}`}>
      <h3>
        <button type="button" aria-expanded={open} aria-controls={panelId} onClick={onToggle}>
          {item.q}
          <TbChevronDown className="nvh-faq-chevron" aria-hidden="true" />
        </button>
      </h3>
      {open && (
        <p id={panelId} className="nvh-faq-answer">
          {item.a}
        </p>
      )}
    </div>
  );
};

const Home = () => {
  const [news, setNews] = useState([]);
  const [openArea, setOpenArea] = useState(null);
  const [openFaq, setOpenFaq] = useState(0);
  const [chooserOpen, setChooserOpen] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await api.get('/news?limit=8');
        setNews(response.data.news || []);
      } catch (error) {
        log.error('Erro ao carregar notícias:', error);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    const scrollToHash = () => {
      const id = window.location.hash.replace('#', '');
      if (!id) return;
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    };
    scrollToHash();
    window.addEventListener('hashchange', scrollToHash);
    return () => window.removeEventListener('hashchange', scrollToHash);
  }, []);

  const openChooser = () => setChooserOpen(true);
  const closeChooser = useCallback(() => setChooserOpen(false), []);

  return (
    <div className="nvh">
      <JsonLd data={FAQ_JSON_LD} />

      <section className="nvh-hero" aria-labelledby="nvh-hero-title">
        <div className="nvh-wrap nvh-hero-grid">
          <div className="nvh-hero-text">
            <div className="nvh-eyebrow nvh-eyebrow-line">Advocacia criminal · Cambé/PR</div>
            <h1 id="nvh-hero-title">Defesa técnica e estratégica em casos de alta complexidade.</h1>
            <p>
              Escritório com atuação estratégica e personalizada, focado na defesa técnica e na proteção dos direitos
              fundamentais.
            </p>
            <div className="nvh-actions">
              <button type="button" className="nvh-btn nvh-btn-gold" onClick={openChooser}>
                <TbBrandWhatsapp aria-hidden="true" />
                Falar com um advogado
              </button>
              <a className="nvh-btn nvh-btn-line" href="#areas-atuacao">Conheça as áreas</a>
            </div>
          </div>
          <div className="nvh-hero-photo">
            <span className="nvh-hero-frame" aria-hidden="true" />
            <Photo
              webp="/advogados.webp"
              png="/advogados.png"
              alt="Os advogados Mateus Bortotto Vieira e Marcos Antônio dos Santos Navarro"
              className="nvh-hero-img"
              loading="eager"
            />
          </div>
        </div>
        <div className="nvh-highlights">
          <div className="nvh-wrap nvh-highlights-grid">
            {HIGHLIGHTS.map((h) => (
              <div key={h.value}>
                <div className="nvh-highlight-value">{h.value}</div>
                <div className="nvh-highlight-label">{h.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="areas-atuacao" className="nvh-section nvh-ivory" aria-labelledby="nvh-areas-title">
        <div className="nvh-wrap">
          <div className="nvh-section-head">
            <div>
              <div className="nvh-eyebrow">Áreas de atuação</div>
              <h2 id="nvh-areas-title">Atuação voltada majoritariamente ao Direito Criminal</h2>
            </div>
            <p>Da investigação aos tribunais superiores, com acompanhamento personalizado em todas as fases.</p>
          </div>
          <div className="nvh-areas">
            {AREAS.map((area) => (
              <AreaCard
                key={area.id}
                area={area}
                open={openArea === area.id}
                onToggle={() => setOpenArea(openArea === area.id ? null : area.id)}
              />
            ))}
          </div>
        </div>
      </section>

      <section id="atendimento" className="nvh-section" aria-labelledby="nvh-atendimento-title">
        <div className="nvh-wrap">
          <div className="nvh-section-head">
            <div>
              <div className="nvh-eyebrow">Atendimento</div>
              <h2 id="nvh-atendimento-title">Como funciona o atendimento</h2>
            </div>
            <p>Do primeiro contato ao fim do caso, você sabe em que etapa está e o que vem a seguir.</p>
          </div>
          <ol className="nvh-steps">
            {STEPS.map((step, index) => {
              const Icon = step.icon;
              return (
                <li key={step.title} className="nvh-step">
                  <div className="nvh-step-top">
                    <Icon className="nvh-step-icon" aria-hidden="true" />
                    <span className="nvh-step-number" aria-hidden="true">
                      {String(index + 1).padStart(2, '0')}
                    </span>
                  </div>
                  <h3>{step.title}</h3>
                  <p>{step.text}</p>
                </li>
              );
            })}
          </ol>
          <div className="nvh-info">
            <div className="nvh-info-card nvh-info-dark">
              <TbClock className="nvh-info-icon" aria-hidden="true" />
              <div>
                <h3>Plantão 24 horas</h3>
                <p>{OFFICE.plantao}</p>
              </div>
            </div>
            <div className="nvh-info-card">
              <TbBuilding className="nvh-info-icon" aria-hidden="true" />
              <div>
                <h3>Atendimento no escritório</h3>
                <p>{OFFICE.hours}</p>
              </div>
            </div>
          </div>
          <p className="nvh-secrecy">
            <TbLock aria-hidden="true" />
            Todo o atendimento é protegido pelo sigilo profissional.
          </p>
        </div>
      </section>

      <section id="sobre-nos" className="nvh-section nvh-ivory" aria-labelledby="nvh-sobre-title">
        <div className="nvh-wrap nvh-about">
          <div className="nvh-about-text">
            <div className="nvh-eyebrow">O escritório</div>
            <h2 id="nvh-sobre-title">Técnica, estratégia e discrição em cada caso</h2>
            <p>
              O escritório <strong>Navarro &amp; Vieira Advogados</strong> é uma sociedade de advogados fundada em janeiro
              de 2020, com atuação voltada majoritariamente ao Direito Criminal, oferecendo assessoria jurídica estratégica,
              defesa técnica qualificada e acompanhamento personalizado em todas as fases da persecução penal.
            </p>
            <p>
              O escritório atua em casos de alta complexidade, incluindo investigações criminais, operações de âmbito
              nacional, ações penais com múltiplos investigados e processos relacionados à criminalidade organizada, crimes
              patrimoniais, tráfico de drogas, lavagem de capitais e demais infrações de competência da Justiça Estadual e
              Federal.
            </p>
            <p>
              Com experiência consolidada perante Tribunais de Justiça, Tribunais Regionais Federais, Superior Tribunal de
              Justiça e Supremo Tribunal Federal, a atuação é marcada pela condução responsável de processos complexos,
              análise jurídica aprofundada e construção de teses defensivas sólidas, sempre com ética, transparência e
              dedicação integral a cada caso.
            </p>
          </div>
          <div className="nvh-pillars">
            {PILLARS.map((p) => (
              <div key={p.title} className="nvh-pillar">
                <h3>{p.title}</h3>
                <p>{p.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="advogados" className="nvh-section" aria-labelledby="nvh-equipe-title">
        <div className="nvh-wrap">
          <div className="nvh-section-head nvh-center">
            <div>
              <div className="nvh-eyebrow">Equipe jurídica</div>
              <h2 id="nvh-equipe-title">Quem conduz a sua defesa</h2>
            </div>
          </div>
          <div className="nvh-team">
            {LAWYERS.map((lawyer) => (
              <article key={lawyer.email} className="nvh-lawyer">
                <Photo webp={lawyer.photoWebp} png={lawyer.photo} alt={lawyer.name} className="nvh-lawyer-photo" />
                <div className="nvh-lawyer-body">
                  <h3>{lawyer.name}</h3>
                  <div className="nvh-lawyer-oab">{lawyer.oab}</div>
                  <ul className="nvh-lawyer-contacts">
                    <li>
                      <TbMail aria-hidden="true" />
                      <a href={`mailto:${lawyer.email}`}>{lawyer.email}</a>
                    </li>
                    <li>
                      <TbPhone aria-hidden="true" />
                      <a href={getTelLink(lawyer.phone)}>{lawyer.phone}</a>
                    </li>
                  </ul>
                  <a
                    className="nvh-btn nvh-btn-navy"
                    href={getWhatsAppLink(lawyer.phone)}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={`Conversar com ${lawyer.name} no WhatsApp`}
                  >
                    <TbBrandWhatsapp aria-hidden="true" />
                    WhatsApp
                  </a>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section id="guias" className="nvh-section nvh-ivory" aria-labelledby="nvh-guias-title">
        <div className="nvh-wrap">
          <div className="nvh-section-head">
            <div>
              <div className="nvh-eyebrow">Guias práticos</div>
              <h2 id="nvh-guias-title">O que fazer quando…</h2>
            </div>
            <Link to="/guias" className="nvh-link">
              Ver todos os guias <TbArrowRight aria-hidden="true" />
            </Link>
          </div>
          <div className="nvh-guides">
            {GUIAS.map((guia) => (
              <GuideCard key={guia.slug} guia={guia} />
            ))}
          </div>
        </div>
      </section>

      <section id="perguntas" className="nvh-section" aria-labelledby="nvh-faq-title">
        <div className="nvh-wrap nvh-faq">
          <div className="nvh-faq-intro">
            <div className="nvh-eyebrow">Dúvidas frequentes</div>
            <h2 id="nvh-faq-title">Perguntas frequentes</h2>
            <p>Respostas gerais e informativas. Cada caso tem particularidades que só a análise de um advogado esclarece.</p>
            <button type="button" className="nvh-btn nvh-btn-gold" onClick={openChooser}>
              <TbBrandWhatsapp aria-hidden="true" />
              Falar com um advogado
            </button>
          </div>
          <div className="nvh-faq-list">
            {FAQ.map((item, index) => (
              <FaqItem
                key={item.q}
                item={item}
                index={index}
                open={openFaq === index}
                onToggle={() => setOpenFaq(openFaq === index ? null : index)}
              />
            ))}
          </div>
        </div>
      </section>

      {news.length > 0 && (
        <section id="noticias" className="nvh-section nvh-ivory" aria-labelledby="nvh-news-title">
          <div className="nvh-wrap">
            <div className="nvh-section-head">
              <div>
                <div className="nvh-eyebrow">Notícias</div>
                <h2 id="nvh-news-title">Artigos e informativos</h2>
              </div>
              <Link to="/noticias" className="nvh-link">
                Ver todas <TbArrowRight aria-hidden="true" />
              </Link>
            </div>
            <div className="nvh-news">
              {news.slice(0, 3).map((item) => (
                <article key={item.id} className="nvh-news-card">
                  <time dateTime={item.created_at}>{new Date(item.created_at).toLocaleDateString('pt-BR')}</time>
                  <h3>{item.title}</h3>
                  {newsExcerpt(item) && <p>{newsExcerpt(item)}</p>}
                  <Link to={`/noticias/${item.id}`} className="nvh-link">
                    Ler notícia <TbArrowRight aria-hidden="true" />
                  </Link>
                </article>
              ))}
            </div>
          </div>
        </section>
      )}

      <section id="contato" className="nvh-contact" aria-labelledby="nvh-contato-title">
        <div className="nvh-wrap nvh-contact-grid">
          <div className="nvh-contact-text">
            <div className="nvh-eyebrow nvh-eyebrow-gold">Contato</div>
            <h2 id="nvh-contato-title">Precisa de orientação jurídica?</h2>
            <p>
              Fale diretamente com um dos advogados. O atendimento é feito com sigilo e análise individual de cada caso.
            </p>
            <ul className="nvh-contact-list">
              <li>
                <TbMapPin aria-hidden="true" />
                <span>
                  {OFFICE.street}
                  <br />
                  {OFFICE.city} · CEP {OFFICE.cep}
                </span>
              </li>
              <li>
                <TbPhone aria-hidden="true" />
                <span>
                  <a href={getTelLink(OFFICE.phone)}>{OFFICE.phone}</a> · Escritório
                </span>
              </li>
              {LAWYERS.map((lawyer) => (
                <li key={lawyer.email}>
                  <TbPhone aria-hidden="true" />
                  <span>
                    <a href={getTelLink(lawyer.phone)}>{lawyer.phone}</a> · {lawyer.name.split(' ')[0]}
                  </span>
                </li>
              ))}
              <li>
                <TbBuilding aria-hidden="true" />
                <span>{OFFICE.hours}</span>
              </li>
              <li>
                <TbClock aria-hidden="true" />
                <span>
                  <strong>Plantão 24 horas:</strong> {OFFICE.plantao.charAt(0).toLowerCase() + OFFICE.plantao.slice(1)}
                </span>
              </li>
            </ul>
            <div className="nvh-actions">
              <button type="button" className="nvh-btn nvh-btn-gold" onClick={openChooser}>
                <TbBrandWhatsapp aria-hidden="true" />
                Falar pelo WhatsApp
              </button>
              <Link to="/contato" className="nvh-btn nvh-btn-line">Enviar mensagem</Link>
            </div>
            <Link to="/golpe-do-falso-advogado" className="nvh-scam">
              <TbAlertTriangle aria-hidden="true" />
              <span>
                <strong>Cuidado com o golpe do falso advogado.</strong> Não pedimos dinheiro, senhas ou dados por mensagem
                e não trocamos de número. Desconfie também de &quot;audiências&quot; por videochamada com falsos juízes ou
                promotores. Saiba como se proteger.
              </span>
            </Link>
          </div>
          <div className="nvh-map">
            <iframe
              title="Mapa do escritório Navarro & Vieira Advogados"
              src="https://www.google.com/maps?q=Rua+B%C3%A9lgica,+437,+Centro,+Camb%C3%A9,+PR,+86181-090&output=embed"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              allowFullScreen
            />
          </div>
        </div>
      </section>

      <LawyerChooser open={chooserOpen} onClose={closeChooser} />
    </div>
  );
};

export default Home;
